import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Palette, Instagram } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { SectionHeader } from "@/components/ui-kit";

export default function Artists() {
  useSeo("Artist Gallery", "Chicago artists painting fence murals for Golden Wolf Foundation. View artist profiles or apply to paint.");
  const [artists, setArtists] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", bio: "", statement: "", social: "", status: "Volunteer" });
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/artists").then((r) => setArtists(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/artists/apply", f); toast.success("Application received — thank you!"); setOpen(false); setF({ name: "", email: "", bio: "", statement: "", social: "", status: "Volunteer" }); }
    catch { toast.error("Submission failed"); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <SectionHeader overline="Artist Gallery" title="Murals of the Movement" sub="Chicago artists cover the Golden Wolf Campus fences with murals of strength, healing and community." />
      <div className="flex gap-3 mb-10">
        <a href="/wishlist" className="btn-outline text-sm" data-testid="donate-paint-btn">Donate Paint</a>
        <button onClick={() => setOpen(true)} className="btn-gold text-sm" data-testid="apply-artist-btn">Apply as an Artist</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {artists.map((a, i) => (
          <motion.div key={a.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08 }}
            className="gw-card overflow-hidden" data-testid={`artist-${a.id}`}>
            <img src={a.image} alt={`Mural by ${a.name}`} className="w-full h-52 object-cover" />
            <div className="p-6">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-xl text-[#FDFBF7]">{a.name}</h3>
                <span className="text-[10px] uppercase tracking-wider text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-0.5">{a.status}</span>
              </div>
              <p className="text-sm text-[#C8C4B7] mt-3">{a.bio}</p>
              <p className="text-sm text-[#8A877B] italic mt-3">"{a.statement}"</p>
            </div>
          </motion.div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-6" onClick={() => setOpen(false)} data-testid="artist-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-[#141414] border border-white/10 w-full max-w-lg p-7 space-y-4">
            <div className="flex items-center gap-2"><Palette className="w-5 h-5 text-[#D4AF37]" /><h3 className="font-heading text-2xl text-[#FDFBF7]">Apply as an Artist</h3></div>
            <div className="grid grid-cols-2 gap-4">
              <input required placeholder="Name *" className="gw-input" data-testid="art-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
              <input type="email" required placeholder="Email *" className="gw-input" data-testid="art-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
            </div>
            <textarea rows={2} placeholder="Short bio" className="gw-input" data-testid="art-bio" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} />
            <textarea rows={2} placeholder="Artist statement" className="gw-input" data-testid="art-statement" value={f.statement} onChange={(e) => setF({ ...f, statement: e.target.value })} />
            <input placeholder="Social links (Instagram, etc.)" className="gw-input" data-testid="art-social" value={f.social} onChange={(e) => setF({ ...f, social: e.target.value })} />
            <select className="gw-input" data-testid="art-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}><option>Volunteer</option><option>Paid</option></select>
            <button disabled={loading} className="btn-gold w-full flex items-center justify-center gap-2 py-3" data-testid="art-submit">{loading && <Loader2 className="w-4 h-4 animate-spin" />} Submit</button>
          </form>
        </div>
      )}
    </div>
  );
}
